import React, { useState, useEffect } from 'react';
import { teamTargetsApi, usersApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, Target, Users } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getHorizonColor } from '../lib/utils';

const HORIZONS = ["Weekly", "15-Day", "30-Day", "6-Month", "1-Year"];

export default function TeamTargets() {
  const [targets, setTargets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [targetsRes, usersRes] = await Promise.all([
        teamTargetsApi.getAll(),
        usersApi.getMembers()
      ]);
      setTargets(targetsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try {
      await teamTargetsApi.delete(id);
      setTargets(targets.filter(t => t.id !== id));
      toast.success('Target deleted');
    } catch (error) {
      toast.error('Failed to delete target');
    }
  };

  const getUserName = (userId) => users.find(u => u.id === userId)?.name || 'Unknown';

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 skeleton rounded-lg" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="team-targets-page">
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Team Targets</h1>
          <p className="page-subtitle">Set and manage team performance targets</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => setEditingTarget(null)} data-testid="create-target-btn">
              <Plus className="w-4 h-4 mr-2" /> Set Target
            </Button>
          </DialogTrigger>
          <TargetDialog
            target={editingTarget}
            users={users}
            existingHorizons={targets.map(t => t.horizon)}
            onClose={() => setDialogOpen(false)}
            onSave={loadData}
          />
        </Dialog>
      </div>

      {/* Targets by Horizon */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {targets.map(target => (
          <Card key={target.id} className="border border-slate-200">
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <Badge className={getHorizonColor(target.horizon)}>{target.horizon}</Badge>
                  <CardTitle className="text-2xl mt-1">{formatCurrency(target.target_value, target.unit)}</CardTitle>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { setEditingTarget(target); setDialogOpen(true); }}
                  data-testid={`edit-target-${target.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(target.id)}
                  data-testid={`delete-target-${target.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Users className="w-4 h-4" />
                  <span>Individual Targets</span>
                </div>
                {Object.entries(target.individual_targets || {}).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(target.individual_targets).map(([userId, value]) => (
                      <div key={userId} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm">{getUserName(userId)}</span>
                        <span className="text-sm font-medium">{formatCurrency(value, target.unit)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No individual targets set</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {targets.length === 0 && (
          <Card className="col-span-full border border-dashed border-slate-300">
            <CardContent className="py-12 text-center">
              <Target className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold mb-2">No team targets set</h3>
              <p className="text-slate-500 mb-4">Create targets to track team performance</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function TargetDialog({ target, users, existingHorizons, onClose, onSave }) {
  const [form, setForm] = useState({
    horizon: target?.horizon || '',
    target_value: target?.target_value || 0,
    unit: target?.unit || '₹',
    individual_targets: target?.individual_targets || {}
  });
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  const availableHorizons = target 
    ? HORIZONS 
    : HORIZONS.filter(h => !existingHorizons.includes(h));

  const updateIndividualTarget = (userId, value) => {
    setForm(prev => ({
      ...prev,
      individual_targets: {
        ...prev.individual_targets,
        [userId]: parseFloat(value) || 0
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.horizon) {
      toast.error('Please select a horizon');
      return;
    }
    
    // Validate target value > 0
    if (!form.target_value || form.target_value <= 0) {
      const error = 'Team target value must be greater than 0';
      setValidationError(error);
      toast.error(error);
      return;
    }
    
    setValidationError('');
    setLoading(true);
    try {
      if (target) {
        await teamTargetsApi.update(target.id, form);
        toast.success('Target updated');
      } else {
        await teamTargetsApi.create(form);
        toast.success('Target created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save target');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{target ? 'Edit Target' : 'Set Team Target'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Horizon</Label>
          <Select 
            value={form.horizon} 
            onValueChange={(v) => setForm({...form, horizon: v})}
            disabled={!!target}
          >
            <SelectTrigger data-testid="target-horizon-select">
              <SelectValue placeholder="Select horizon" />
            </SelectTrigger>
            <SelectContent>
              {availableHorizons.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Team Target</Label>
            <Input
              type="number"
              value={form.target_value}
              onChange={(e) => setForm({...form, target_value: parseFloat(e.target.value) || 0})}
              required
              data-testid="target-value-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Unit</Label>
            <Select value={form.unit} onValueChange={(v) => setForm({...form, unit: v})}>
              <SelectTrigger data-testid="target-unit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="₹">₹ (INR)</SelectItem>
                <SelectItem value="count">Count</SelectItem>
                <SelectItem value="%">Percent</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Individual Targets (optional)</Label>
          <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
            {users.map(user => (
              <div key={user.id} className="flex items-center justify-between p-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                    <span className="text-sm font-semibold">{user.name.charAt(0)}</span>
                  </div>
                  <span className="text-sm">{user.name}</span>
                </div>
                <Input
                  type="number"
                  className="w-32 h-8"
                  placeholder="Target"
                  value={form.individual_targets[user.id] || ''}
                  onChange={(e) => updateIndividualTarget(user.id, e.target.value)}
                  data-testid={`individual-target-${user.id}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-target-btn">
            {loading ? 'Saving...' : 'Save Target'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
